import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, MetricCard, Panel, SearchField, SegmentedControl, StatusChip, ToolbarButton,
  type ListColumn, type StatusTone,
} from '../../../ui-kit';
import { fetchCcCalls, type CcCall, type CcCallsByDay } from '../../../services/carrierCorrespondenceService';
import AgentName from '../rocco/AgentName';
import { lengthOf, matches, useLive, when } from '../rocco/live';
import CaseStory from './CaseStory';

/**
 * Every call made to a carrier, newest first.
 *
 * ROCCO's Conversations page, adapted: the same four tiles with a week's trend,
 * the same columns (time, who, agent, how it went, length, outcome), the same
 * filter, and a row that opens straight onto that call's transcript. Only the
 * words change, because what a carrier call produces is different from what a
 * lead call does.
 */

type Filter = 'all' | 'answered' | 'no_answer' | 'needs_person';

const MATCHES: Record<Filter, (c: CcCall) => boolean> = {
  all: () => true,
  answered: (c) => c.status === 'answered',
  no_answer: (c) => c.status === 'no answer' || c.status === 'failed',
  needs_person: (c) => c.status === 'needs_person',
};

const HOW: Record<string, [StatusTone, string]> = {
  ringing: ['good', 'Ringing'],
  'in progress': ['good', 'On the call'],
  ended: ['neutral', 'Call ended, reading it'],
  answered: ['good', 'Answered'],
  needs_person: ['bad', 'Needs a person'],
  'no answer': ['neutral', 'No answer'],
  failed: ['warning', "Didn't go through"],
};
const how = (status: string): [StatusTone, string] => HOW[status] ?? ['neutral', status];

export default function ConversationsPage() {
  const { data, error, loading, reconnecting } = useLive(fetchCcCalls);
  const [filter, setFilter] = React.useState<Filter>('all');
  const [open, setOpen] = React.useState<{ caseId: string; callId: string } | null>(null);
  const [query, setQuery] = React.useState('');

  const calls = data?.calls ?? [];
  const days = data?.by_day ?? [];
  const rows = calls.filter(MATCHES[filter])
    .filter((c) => matches(query, [c.applicant ?? null, c.policy_num ?? null, c.carrier ?? null,
      c.agent, how(c.status)[1], c.outcome]));
  const countOf = (f: Filter) => calls.filter(MATCHES[f]).length;
  const openCall = (c: CcCall) => c.case_id && setOpen({ caseId: c.case_id, callId: c.id });

  const columns: ListColumn<CcCall>[] = [
    {
      key: 'time',
      sortValue: (c) => c.started_at, sortStartsDescending: true,
      header: 'Time',
      width: 130,
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>
          {when(c.started_at)}
        </Typography>
      ),
    },
    {
      key: 'case',
      sortValue: (c) => c.applicant ?? '',
      header: 'Case',
      grow: 1,
      minWidth: 140,
      render: (c) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
            {c.applicant ?? 'Unknown case'}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
            {c.carrier}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'agent',
      sortValue: (c) => c.agent,
      header: 'Agent',
      width: 120,
      render: (c) => (c.agent ? <AgentName person={{ name: c.agent, kind: 'ai' }} /> : null),
    },
    {
      key: 'how',
      sortValue: (c) => how(c.status)[1],
      header: 'How it went',
      width: 170,
      render: (c) => <StatusChip tone={how(c.status)[0]} label={how(c.status)[1]} pulse={c.live} />,
    },
    {
      key: 'length',
      sortValue: (c) => (c.live ? null : c.duration_secs), sortStartsDescending: true,
      header: 'Length',
      width: 70,
      align: 'right',
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>
          {c.live ? '—' : lengthOf(c.duration_secs)}
        </Typography>
      ),
    },
    {
      key: 'outcome',
      sortValue: (c) => c.outcome,
      header: 'Outcome',
      grow: 1.4,
      minWidth: 160,
      render: (c) => (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
          {c.outcome || (c.live ? 'In progress' : '—')}
        </Typography>
      ),
    },
    {
      key: 'open',
      header: '',
      width: 150,
      align: 'right',
      render: (c) => (c.case_id && c.has_transcript ? (
        <ToolbarButton variant="ghost" onClick={() => openCall(c)}>Show Transcript</ToolbarButton>
      ) : null),
    },
  ];

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(c) => c.id}
      pageSize={25}
      loading={loading}
      defaultSort={{ key: 'time', direction: 'desc' }}
      onRowClick={openCall}
      emptyMessage={calls.length
        ? 'Nothing matches. Clear the search or the filter.'
        : 'No calls yet. The first appears here the moment the AI rings a carrier.'}
    />
  );
  // Search beside the title, the switch on the left of the toolbar, the
  // download on its right (the panel header's two rows, 21 Oct 2026).
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search calls…" />;
  const picker = (
    <SegmentedControl
      value={filter}
      onChange={(next) => setFilter(next)}
      ariaLabel="Which calls"
      options={[
        { value: 'all', label: `All · ${countOf('all')}` },
        { value: 'answered', label: `Answered · ${countOf('answered')}` },
        { value: 'no_answer', label: `No answer · ${countOf('no_answer')}` },
        { value: 'needs_person', label: `Needs a person · ${countOf('needs_person')}` },
      ]}
    />
  );

  const tile = (key: keyof Omit<CcCallsByDay, 'day'>, label: string, info: string, filterTo: Filter) => (
    <MetricCard
      loading={loading}
      label={label}
      value={calls.filter(MATCHES[filterTo]).length.toLocaleString()}
      info={info}
      trend={{
        points: days.map((d, i) => ({
          key: d.day,
          label: i === days.length - 1 ? 'Today' : new Date(`${d.day}T12:00:00Z`)
            .toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
          value: d[key],
        })),
        formatValue: (v: number) => v.toLocaleString(),
        label: `${label} by day, last 7 days`,
      }}
    />
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        {tile('calls', 'Calls made by AI', 'Every call the AI has placed to a carrier, answered or not.', 'all')}
        {tile('answered', 'Answered by AI', 'Calls where the carrier said what is outstanding.', 'answered')}
        {tile('no_answer', 'No answer', 'Calls nobody picked up. The case stays stalled and can be called again.', 'no_answer')}
        {tile('needs_person', 'Needs a person', 'Calls the carrier would not settle, each with the reason.', 'needs_person')}
      </Box>

      <Panel
        title="Conversations"
        subtitle={`${calls.length} calls · newest first · ${reconnecting ? 'Reconnecting…' : 'a call appears as it is dialled and updates as it goes'}`}
        search={searchBox}
        tools={picker}
        expanded={{ content: table, actions: <>{searchBox}{picker}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : table}
      </Panel>

      {open ? <CaseStory caseId={open.caseId} callId={open.callId} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
